"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.setTransferRestrictions = exports.prepareStorage = exports.getAuthorization = exports.prepareSetTransferRestrictions = exports.addExemptionIfNotPresent = exports.newExemptionRecord = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const lodash_1 = require("lodash");
const internal_1 = require("../../internal");
const types_1 = require("../../types");
const conversion_1 = require("../../utils/conversion");
const internal_2 = require("../../utils/internal");
const newExemptionRecord = () => ({
    Accredited: [],
    Affiliate: [],
    Jurisdiction: [],
    None: [],
});
exports.newExemptionRecord = newExemptionRecord;
/**
 * @hidden
 * @param toInsertId - Identity to insert into the exemption records
 * @param exemptionRecords - records that will be mutated if the Identity should be added
 * @param claimType - type of Claim the Identity is exempt from
 * @param filterSet - set of Identities that should not be added into exemption records if given
 */
const addExemptionIfNotPresent = (toInsertId, exemptionRecords, claimType, filterSet = []) => {
    const found = filterSet.some(currentId => currentId.did === toInsertId.did);
    if (!found) {
        exemptionRecords[claimType].push(toInsertId);
    }
};
exports.addExemptionIfNotPresent = addExemptionIfNotPresent;
/**
 * @hidden
 * Checks if the input restrictions are valid
 */
function assertInputValid(inputRestrictions, type) {
    if ([types_1.TransferRestrictionType.Percentage, types_1.TransferRestrictionType.Count].includes(type) &&
        inputRestrictions.length > 1) {
        throw new internal_1.PolymeshError({
            code: types_1.ErrorCode.ValidationError,
            message: 'Only one of provided Transfer Type Restriction can be set at a time',
            data: { type },
        });
    }
    if (type === types_1.TransferRestrictionType.ClaimCount) {
        const seenJurisdictions = new Set();
        const seenClaimTypes = new Set();
        inputRestrictions.forEach(restriction => {
            var _a;
            const { claim } = restriction;
            if (claim.type === types_1.ClaimType.Jurisdiction) {
                // cannot add two claims with same country code restrictions will result in internal error
                if (seenJurisdictions.has(claim.countryCode || 'none')) {
                    throw new internal_1.PolymeshError({
                        code: types_1.ErrorCode.ValidationError,
                        message: 'Duplicate Jurisdiction CountryCode found in input',
                        data: { countryCode: claim.countryCode },
                    });
                }
                seenJurisdictions.add((_a = claim.countryCode) !== null && _a !== void 0 ? _a : 'none');
            }
            else {
                // cannot add two ClaimType.Accredited or ClaimType.Affiliate restrictions will result in internal error
                if (seenClaimTypes.has(claim.type)) {
                    throw new internal_1.PolymeshError({
                        code: types_1.ErrorCode.ValidationError,
                        message: 'Duplicate ClaimType found in input',
                        data: { claimType: claim.type },
                    });
                }
                seenClaimTypes.add(claim.type);
            }
        });
    }
}
/**
 * @hidden
 *
 * Calculates the smallest change needed to get to the requested state from the current state.
 * While clearing and building the restrictions would be simpler, it would be inefficient use of gas fees
 */
function transformInput(inputRestrictions, currentRestrictions, currentExemptions, type, context) {
    const toSetExemptions = (0, exports.newExemptionRecord)();
    const toRemoveExemptions = (0, exports.newExemptionRecord)();
    let needDifferentConditions = inputRestrictions.length !== currentRestrictions.length;
    const conditions = [];
    const inputExemptions = (0, exports.newExemptionRecord)();
    // for each restriction check if we need to make a condition and track all exemptions
    inputRestrictions.forEach(restriction => {
        var _a;
        let value;
        let claimType;
        if ('count' in restriction) {
            value = restriction.count;
        }
        else if ('percentage' in restriction) {
            value = restriction.percentage;
        }
        else {
            value = restriction;
            claimType = restriction.claim.type;
        }
        const condition = { type, value };
        const compareConditions = (transferCondition) => (0, internal_2.compareTransferRestrictionToInput)(transferCondition, condition);
        if (!needDifferentConditions) {
            needDifferentConditions = ![...currentRestrictions].find(compareConditions);
        }
        const rawCondition = (0, conversion_1.transferRestrictionToPolymeshTransferCondition)(condition, context);
        conditions.push(rawCondition);
        (_a = restriction.exemptedIdentities) === null || _a === void 0 ? void 0 : _a.forEach(exemption => {
            const key = claimType || 'None';
            const id = (0, internal_2.asIdentity)(exemption, context);
            (0, exports.addExemptionIfNotPresent)(id, inputExemptions, key);
        });
    });
    // calculate exemptions to add — for each input exemption check if it already exists
    (0, lodash_1.forEach)(inputExemptions, (toAddIds, cType) => {
        const key = cType;
        const currentIds = currentExemptions[key];
        toAddIds.forEach(id => {
            (0, exports.addExemptionIfNotPresent)(id, toSetExemptions, key, currentIds);
        });
    });
    // calculate exemptions to remove — for each current exemption check if it was in the input
    (0, lodash_1.forEach)(currentExemptions, (currentIds, cType) => {
        const key = cType;
        const given = inputExemptions[key];
        currentIds.forEach(id => {
            (0, exports.addExemptionIfNotPresent)(id, toRemoveExemptions, key, given);
        });
    });
    const sizeOf = (obj) => (0, lodash_1.flatten)((0, lodash_1.entries)(obj).map(([, identities]) => identities)).length;
    const needDifferentExemptions = sizeOf(toSetExemptions) > 0 || sizeOf(toRemoveExemptions) > 0;
    if (!needDifferentConditions && !needDifferentExemptions) {
        throw new internal_1.PolymeshError({
            code: types_1.ErrorCode.NoDataChange,
            message: inputRestrictions.length
                ? 'The restrictions and exemptions are already in place'
                : 'No restrictions to remove',
        });
    }
    return { conditions, toSetExemptions, toRemoveExemptions };
}
/**
 * @hidden
 */
function prepareSetTransferRestrictions(args) {
    return __awaiter(this, void 0, void 0, function* () {
        const { context: { polymeshApi: { tx: { statistics }, consts, }, }, storage: { occupiedSlots, currentExemptions, filteredRestrictions, currentTypeRestrictions }, context, } = this;
        const { restrictions: { length: newRestrictionAmount }, restrictions, type, asset, } = args;
        const rawAssetId = (0, conversion_1.assetToMeshAssetId)(asset, context);
        assertInputValid(restrictions, type);
        const { conditions, toSetExemptions, toRemoveExemptions } = transformInput(restrictions, currentTypeRestrictions, currentExemptions, type, context);
        const maxTransferConditions = (0, conversion_1.u32ToBigNumber)(consts.statistics.maxTransferConditionsPerAsset);
        const finalCount = occupiedSlots.plus(newRestrictionAmount);
        if (finalCount.isGreaterThan(maxTransferConditions)) {
            throw new internal_1.PolymeshError({
                code: types_1.ErrorCode.LimitExceeded,
                message: 'Cannot set more Transfer Restrictions than there are slots available',
                data: {
                    availableSlots: maxTransferConditions.minus(occupiedSlots),
                },
            });
        }
        const transactions = [];
        const op = (0, conversion_1.transferRestrictionTypeToStatOpType)(type, context);
        transactions.push((0, internal_2.checkTxType)({
            transaction: statistics.setAssetTransferCompliance,
            args: [
                rawAssetId,
                (0, conversion_1.complianceConditionsToBtreeSet)([...filteredRestrictions, ...conditions], context),
            ],
        }));
        const pushExemptions = (exemptions, exempt) => {
            (0, lodash_1.forEach)(exemptions, (exempted, claimType) => {
                if (exempted.length === 0) {
                    return;
                }
                const rawClaimType = claimType === 'None' ? undefined : claimType;
                const exemptKey = (0, conversion_1.toExemptKey)(rawAssetId, op, rawClaimType);
                const exemptedBtreeSet = (0, conversion_1.identitiesToBtreeSet)(exempted, context);
                const rawExempt = (0, conversion_1.booleanToBool)(exempt, context);
                transactions.push((0, internal_2.checkTxType)({
                    transaction: statistics.setEntitiesExempt,
                    feeMultiplier: new bignumber_js_1.default(exemptedBtreeSet.size),
                    args: [rawExempt, exemptKey, exemptedBtreeSet],
                }));
            });
        };
        pushExemptions(toSetExemptions, true);
        pushExemptions(toRemoveExemptions, false);
        return { transactions, resolver: finalCount };
    });
}
exports.prepareSetTransferRestrictions = prepareSetTransferRestrictions;
/**
 * @hidden
 */
function getAuthorization({ asset, restrictions }) {
    const transactions = [types_1.TxTags.statistics.SetAssetTransferCompliance];
    const needExemptionsPermission = restrictions.some(r => { var _a; return (_a = r.exemptedIdentities) === null || _a === void 0 ? void 0 : _a.length; });
    if (needExemptionsPermission) {
        transactions.push(types_1.TxTags.statistics.SetEntitiesExempt);
    }
    return {
        permissions: {
            assets: [asset],
            transactions,
            portfolios: [],
        },
    };
}
exports.getAuthorization = getAuthorization;
/**
 * @hidden
 */
function prepareStorage(args) {
    return __awaiter(this, void 0, void 0, function* () {
        const { context, context: { polymeshApi: { query: { statistics }, }, }, } = this;
        const { asset, type } = args;
        const rawAssetId = (0, conversion_1.assetToMeshAssetId)(asset, context);
        const [currentStats, { requirements: currentRestrictions }] = yield (0, internal_2.requestMulti)(context, [
            [statistics.activeAssetStats, rawAssetId],
            [statistics.assetTransferCompliances, rawAssetId],
        ]);
        args.restrictions.forEach(restriction => {
            let claimIssuer;
            if (type === types_1.TransferRestrictionType.ClaimCount ||
                type === types_1.TransferRestrictionType.ClaimPercentage) {
                const { claim: { type: claimType }, issuer, } = restriction;
                claimIssuer = { claimType, issuer };
            }
            const neededStat = (0, internal_2.neededStatTypeForRestrictionInput)({ type, claimIssuer }, context);
            (0, internal_2.assertStatIsSet)(currentStats, neededStat);
        });
        const filteredRestrictions = [...currentRestrictions].filter(requirement => {
            if (type === types_1.TransferRestrictionType.Count) {
                return !requirement.isMaxInvestorCount;
            }
            else if (type === types_1.TransferRestrictionType.Percentage) {
                return !requirement.isMaxInvestorOwnership;
            }
            else if (type === types_1.TransferRestrictionType.ClaimCount) {
                return !requirement.isClaimCount;
            }
            else {
                return !requirement.isClaimOwnership;
            }
        });
        const currentTypeRestrictions = [...currentRestrictions].filter(requirement => {
            if (type === types_1.TransferRestrictionType.Count) {
                return requirement.isMaxInvestorCount;
            }
            else if (type === types_1.TransferRestrictionType.Percentage) {
                return requirement.isMaxInvestorOwnership;
            }
            else if (type === types_1.TransferRestrictionType.ClaimCount) {
                return requirement.isClaimCount;
            }
            else {
                return requirement.isClaimOwnership;
            }
        });
        const op = (0, conversion_1.transferRestrictionTypeToStatOpType)(type, context);
        const claimTypes = type === types_1.TransferRestrictionType.Count || type === types_1.TransferRestrictionType.Percentage
            ? [undefined]
            : [types_1.ClaimType.Accredited, types_1.ClaimType.Affiliate, types_1.ClaimType.Jurisdiction];
        const exemptionFetchers = [];
        claimTypes.forEach(claimType => {
            exemptionFetchers.push(statistics.transferConditionExemptEntities.entries((0, conversion_1.toExemptKey)(rawAssetId, op, claimType)));
        });
        const rawCurrentExemptions = (0, lodash_1.flatten)(yield Promise.all(exemptionFetchers));
        const currentExemptions = (0, exports.newExemptionRecord)();
        rawCurrentExemptions.forEach(([{ args: [{ claimType: rawClaimType }, rawDid], },]) => {
            const did = (0, conversion_1.identityIdToString)(rawDid);
            let claimType = 'None';
            if (rawClaimType.isSome) {
                claimType = (0, conversion_1.meshClaimTypeToClaimType)(rawClaimType.unwrap());
            }
            const identity = new internal_1.Identity({ did }, context);
            (0, exports.addExemptionIfNotPresent)(identity, currentExemptions, claimType);
        });
        return {
            occupiedSlots: new bignumber_js_1.default(filteredRestrictions.length),
            currentTypeRestrictions,
            filteredRestrictions,
            currentExemptions,
        };
    });
}
exports.prepareStorage = prepareStorage;
/**
 * @hidden
 */
const setTransferRestrictions = () => new internal_1.Procedure(prepareSetTransferRestrictions, getAuthorization, prepareStorage);
exports.setTransferRestrictions = setTransferRestrictions;
//# sourceMappingURL=setTransferRestrictions.js.map